import { useState, useCallback, useEffect } from 'react';

/**
 * Pagination state and handlers for data tables
 */
export interface TablePaginationState {
  page: number;
  pageSize: number;
}

export interface UseTablePaginationOptions {
  initialPage?: number;
  initialPageSize?: number;
  pageSizeOptions?: number[];
}

export interface UseTablePaginationReturn {
  // State
  page: number;
  pageSize: number;
  
  // Handlers
  setPage: (page: number) => void;
  setPageSize: (size: number) => void;
  handlePageChange: (page: number) => void;
  handlePageSizeChange: (size: number) => void;
  resetPagination: () => void;
  
  // Options
  pageSizeOptions: number[];
}

/**
 * Hook for managing pagination state in data tables
 * 
 * @param options - Configuration options
 * @returns Pagination state and handlers
 * 
 * @example
 * ```tsx
 * const { page, pageSize, handlePageChange, handlePageSizeChange } = useTablePagination({
 *   initialPage: 0,
 *   initialPageSize: 50,
 * });
 * ```
 */
export const useTablePagination = (
  options: UseTablePaginationOptions = {}
): UseTablePaginationReturn => {
  const {
    initialPage = 0,
    initialPageSize = 50,
    pageSizeOptions = [25, 50, 100, 200],
  } = options;

  const [page, setPage] = useState(initialPage);
  const [pageSize, setPageSize] = useState(initialPageSize);

  // Handle page change
  const handlePageChange = useCallback((newPage: number) => {
    setPage(newPage);
  }, []);

  // Handle page size change (reset to first page)
  const handlePageSizeChange = useCallback((newSize: number) => {
    setPageSize(newSize);
    setPage(0); // Reset to first page when changing page size
  }, []);

  // Reset pagination to initial state
  const resetPagination = useCallback(() => {
    setPage(initialPage);
    setPageSize(initialPageSize);
  }, [initialPage, initialPageSize]);

  return {
    page,
    pageSize,
    setPage,
    setPageSize,
    handlePageChange,
    handlePageSizeChange,
    resetPagination,
    pageSizeOptions,
  };
};

/**
 * Hook for managing debounced search with pagination reset
 * 
 * @param debounceMs - Debounce delay in milliseconds
 * @returns Search state and handlers
 */
export interface UseSearchWithPaginationReturn {
  searchTerm: string;
  debouncedSearchTerm: string;
  setSearchTerm: (term: string) => void;
  clearSearch: () => void;
}

export const useSearchWithPagination = (
  debounceMs: number = 1000,
  onSearchChange?: () => void
): UseSearchWithPaginationReturn => {
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');

  // Debounce effect
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchTerm !== debouncedSearchTerm) {
        setDebouncedSearchTerm(searchTerm);
        if (onSearchChange) {
          onSearchChange();
        }
      }
    }, debounceMs);

    return () => clearTimeout(timer);
  }, [searchTerm, debounceMs]); // Intentionally excluding debouncedSearchTerm to prevent loops

  // Clear search
  const clearSearch = useCallback(() => {
    setSearchTerm('');
    setDebouncedSearchTerm('');
    if (onSearchChange) {
      onSearchChange();
    }
  }, [onSearchChange]);

  return {
    searchTerm,
    debouncedSearchTerm,
    setSearchTerm,
    clearSearch,
  };
};

export default useTablePagination;
